import { useState, useEffect } from 'react'
import { Loader2, Plus, Building2, RefreshCw, MapPin, Monitor } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Textarea } from '@/components/ui/textarea'
import { useAuthContext } from '../contexts/AuthContext'
import clientService, { 
  Client, 
  Location, 
  CreateClientRequest, 
  CreateLocationRequest 
} from '../services/clientService'

export default function ClientManagement() {
  const { hasAnyGroup, user } = useAuthContext()
  const [clients, setClients] = useState<Client[]>([])
  const [locations, setLocations] = useState<Record<string, Location[]>>({})
  const [deviceCounts, setDeviceCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const [clientDialogOpen, setClientDialogOpen] = useState(false)
  const [locationDialogOpen, setLocationDialogOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [creating, setCreating] = useState(false)
  
  const [clientForm, setClientForm] = useState<CreateClientRequest>({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
  })
  
  const [locationForm, setLocationForm] = useState<CreateLocationRequest>({
    clientId: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    contactName: '',
    contactPhone: '',
  })
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  
  const hasPermission = hasAnyGroup(['superAdmin', 'groupAdmin'])

  const loadClients = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const clientsData = await clientService.listClients()
      setClients(clientsData)
      
      const locationsMap: Record<string, Location[]> = {}
      const deviceCountsMap: Record<string, number> = {}
      
      for (const client of clientsData) {
        const clientLocations = await clientService.listClientLocations(client.id)
        locationsMap[client.id] = clientLocations
        
        const devices = await clientService.getClientDevices(client.id)
        deviceCountsMap[client.id] = devices.length
      }
      
      setLocations(locationsMap)
      setDeviceCounts(deviceCountsMap)
    } catch (err) {
      console.error('Error loading clients:', err)
      setError('Failed to load clients. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hasPermission) {
      loadClients()
    }
  }, [hasPermission])

  const handleCreateClient = () => {
    setClientForm({
      name: '',
      description: '',
      contactEmail: '',
      contactPhone: '',
    })
    setFormErrors({})
    setClientDialogOpen(true)
  }

  const validateClientForm = (): boolean => {
    const errors: Record<string, string> = {}
    
    if (!clientForm.name.trim()) {
      errors.name = 'Client name is required'
    }
    
    if (clientForm.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientForm.contactEmail)) {
      errors.contactEmail = 'Invalid email format'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateLocationForm = (): boolean => {
    const errors: Record<string, string> = {}
    
    if (!locationForm.name.trim()) {
      errors.name = 'Location name is required'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmitClient = async () => {
    if (!validateClientForm()) return

    try {
      setCreating(true)
      setError(null)
      
      const userId = user?.userId || user?.username || 'unknown'
      
      const newClient = await clientService.createClient(clientForm, userId)
      
      setClients(prev => [newClient, ...prev])
      setLocations(prev => ({ ...prev, [newClient.id]: [] }))
      setDeviceCounts(prev => ({ ...prev, [newClient.id]: 0 }))
      setClientDialogOpen(false)
    } catch (err) {
      console.error('Error creating client:', err)
      setError(err instanceof Error ? err.message : 'Failed to create client')
    } finally {
      setCreating(false)
    }
  }

  const handleCreateLocation = (client: Client) => {
    setSelectedClient(client)
    setLocationForm({
      clientId: client.id,
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      contactName: '',
      contactPhone: '',
    })
    setFormErrors({})
    setLocationDialogOpen(true)
  }

  const handleSubmitLocation = async () => {
    if (!validateLocationForm()) return

    try {
      setCreating(true)
      setError(null)
      
      const newLocation = await clientService.createLocation(locationForm)
      
      setLocations(prev => ({
        ...prev,
        [locationForm.clientId]: [...(prev[locationForm.clientId] || []), newLocation]
      }))
      
      setLocationDialogOpen(false)
      setSelectedClient(null)
    } catch (err) {
      console.error('Error creating location:', err)
      setError(err instanceof Error ? err.message : 'Failed to create location')
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!hasPermission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Building2 className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-2xl font-bold">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to manage clients.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">Client & Location Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadClients}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={handleCreateClient}>
            <Plus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Clients Yet</h3>
            <p className="text-sm text-muted-foreground mb-6 text-center">
              Create your first client to start organizing devices and locations.
            </p>
            <Button onClick={handleCreateClient}>
              <Plus className="mr-2 h-4 w-4" />
              Create First Client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" defaultValue={clients.map(c => c.id)} className="space-y-4">
          {clients.map((client) => (
            <AccordionItem key={client.id} value={client.id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-4 w-full">
                  <Building2 className="h-5 w-5 text-primary" />
                  <div className="flex-1 text-left">
                    <h3 className="text-lg font-semibold">{client.name}</h3>
                    {client.description && (
                      <p className="text-sm text-muted-foreground">{client.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="gap-1">
                      <Monitor className="h-3 w-3" />
                      {deviceCounts[client.id] || 0} devices
                    </Badge>
                    <Badge variant="secondary" className="gap-1">
                      <MapPin className="h-3 w-3" />
                      {locations[client.id]?.length || 0} locations
                    </Badge>
                    {!client.isActive && (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                {/* Client Details */}
                {(client.contactEmail || client.contactPhone) && (
                  <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
                    {client.contactEmail && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Contact Email</p>
                        <p className="text-sm">{client.contactEmail}</p>
                      </div>
                    )}
                    {client.contactPhone && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Contact Phone</p>
                        <p className="text-sm">{client.contactPhone}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Locations */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-semibold">Locations</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCreateLocation(client)}
                    >
                      <Plus className="mr-2 h-3 w-3" />
                      Add Location
                    </Button>
                  </div>

                  {locations[client.id]?.length === 0 ? (
                    <div className="border border-dashed rounded-lg p-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        No locations yet. Add a location to start deploying devices.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {locations[client.id]?.map((location) => (
                        <Card key={location.id}>
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <h5 className="font-semibold">{location.name}</h5>
                            </div>
                            {location.address && (
                              <p className="text-sm text-muted-foreground mb-1">
                                {location.address}
                              </p>
                            )}
                            {(location.city || location.state) && (
                              <p className="text-sm text-muted-foreground">
                                {[location.city, location.state].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {location.contactName && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Contact: {location.contactName}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Create Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clientName">Client Name *</Label>
              <Input
                id="clientName"
                autoFocus
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                className={formErrors.name ? 'border-destructive' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-destructive">{formErrors.name}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={2}
                value={clientForm.description}
                onChange={(e) => setClientForm({ ...clientForm, description: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                type="email"
                value={clientForm.contactEmail}
                onChange={(e) => setClientForm({ ...clientForm, contactEmail: e.target.value })}
                className={formErrors.contactEmail ? 'border-destructive' : ''}
              />
              {formErrors.contactEmail && (
                <p className="text-sm text-destructive">{formErrors.contactEmail}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                value={clientForm.contactPhone}
                onChange={(e) => setClientForm({ ...clientForm, contactPhone: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientDialogOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleSubmitClient} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Client'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Location Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Location to {selectedClient?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="locationName">Location Name *</Label>
              <Input
                id="locationName"
                autoFocus
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                placeholder="e.g., Main Office, Warehouse, Store #1"
                className={formErrors.name ? 'border-destructive' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-destructive">{formErrors.name}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={locationForm.address}
                onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                placeholder="Street address"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={locationForm.city}
                  onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={locationForm.state}
                  onChange={(e) => setLocationForm({ ...locationForm, state: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={locationForm.country}
                  onChange={(e) => setLocationForm({ ...locationForm, country: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={locationForm.postalCode}
                  onChange={(e) => setLocationForm({ ...locationForm, postalCode: e.target.value })}
                />
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold mb-3">Location Contact (Optional)</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contactName">Contact Name</Label>
                  <Input
                    id="contactName"
                    value={locationForm.contactName}
                    onChange={(e) => setLocationForm({ ...locationForm, contactName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="locationContactPhone">Contact Phone</Label>
                  <Input
                    id="locationContactPhone"
                    value={locationForm.contactPhone}
                    onChange={(e) => setLocationForm({ ...locationForm, contactPhone: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialogOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleSubmitLocation} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Location'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
